/**
 * Workflow Graph Analyzer
 *
 * Utilities for analyzing workflow graphs to find upstream nodes,
 * extract variables, and support dynamic property configuration.
 */

import { Node, Edge } from 'reactflow';

/**
 * Variable metadata from a compose_schema node
 */
export interface ComposeSchemaVariable {
  name: string;           // outputVariable value
  nodeId: string;         // Node ID that creates this variable
  schemaId: string;       // Schema ID from config
  groupId: string;        // Group ID from config
}

/**
 * Get all nodes that execute before a target node
 * Uses BFS backwards traversal through edges
 *
 * @param nodes - All workflow nodes
 * @param edges - All workflow edges
 * @param targetNodeId - The node to start from
 * @returns Array of upstream nodes
 */
export function getUpstreamNodes(
  nodes: Node[],
  edges: Edge[],
  targetNodeId: string
): Node[] {
  const upstreamNodes: Node[] = [];
  const visited = new Set<string>();
  const queue: string[] = [targetNodeId];

  // Mark target as visited so we don't include it
  visited.add(targetNodeId);

  while (queue.length > 0) {
    const currentId = queue.shift()!;

    // Find all incoming edges to current node
    const incomingEdges = edges.filter(edge => edge.target === currentId);

    for (const edge of incomingEdges) {
      const sourceId = edge.source;

      // Skip if already visited (prevents infinite loops on circular refs)
      if (visited.has(sourceId)) {
        continue;
      }

      visited.add(sourceId);

      // Find the source node
      const sourceNode = nodes.find(n => n.id === sourceId);

      if (sourceNode) {
        upstreamNodes.push(sourceNode);
        // Add to queue to continue traversal
        queue.push(sourceId);
      }
    }
  }

  return upstreamNodes;
}

/**
 * Extract compose_schema variables from upstream nodes
 *
 * Analyzes the workflow graph to find all compose_schema nodes
 * that execute before the target node, then extracts their output variables.
 *
 * @param nodes - All workflow nodes
 * @param edges - All workflow edges
 * @param targetNodeId - The schema_data_store node ID
 * @returns Array of available compose_schema variables
 */
export function getComposeSchemaVariables(
  nodes: Node[],
  edges: Edge[],
  targetNodeId: string
): ComposeSchemaVariable[] {
  // Get all upstream nodes
  const upstreamNodes = getUpstreamNodes(nodes, edges, targetNodeId);

  // Filter to compose_schema nodes and extract variables
  const variables: ComposeSchemaVariable[] = [];

  for (const node of upstreamNodes) {
    // Check if this is a compose_schema node
    // NOTE: In ReactFlow, custom nodes have type set to the node type string
    // But the actual workflow node type is in node.data.type
    const nodeType = node.data?.type || node.type;

    if (nodeType === 'compose_schema' && node.data?.config) {
      const config = node.data.config;

      // Extract variable information
      const outputVariable = config.outputVariable;
      const schemaId = config.schemaId;
      const groupId = config.groupId;

      // Only require outputVariable for dropdown display
      // schemaId and groupId are optional (used later for fetching schema fields)
      if (outputVariable) {
        variables.push({
          name: outputVariable,
          nodeId: node.id,
          schemaId: schemaId || '',
          groupId: groupId || '',
        });
      }
    }
  }

  return variables;
}

/**
 * Check if a node has compose_schema variables upstream
 *
 * Useful for determining if variable dropdowns should be enabled
 *
 * @param nodes - All workflow nodes
 * @param edges - All workflow edges
 * @param targetNodeId - The node to check
 * @returns True if compose_schema variables are available
 */
export function hasComposeSchemaUpstream(
  nodes: Node[],
  edges: Edge[],
  targetNodeId: string
): boolean {
  const variables = getComposeSchemaVariables(nodes, edges, targetNodeId);
  return variables.length > 0;
}

/**
 * Get the compose_schema node that outputs a specific variable
 *
 * @param nodes - All workflow nodes
 * @param edges - All workflow edges
 * @param targetNodeId - The schema_data_store node ID
 * @param variableName - The variable name to find
 * @returns The compose_schema variable metadata, or null if not found
 */
export function findComposeSchemaVariable(
  nodes: Node[],
  edges: Edge[],
  targetNodeId: string,
  variableName: string
): ComposeSchemaVariable | null {
  const variables = getComposeSchemaVariables(nodes, edges, targetNodeId);
  return variables.find(v => v.name === variableName) || null;
}
